// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

// about vue-router
// MIT License
//
// Copyright (c) 2013-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { mapActions, useStore } from 'vuex';
import { ref, computed, getCurrentInstance, toRefs, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave, } from 'vue-router';

import fullModal from "../../../../share/components/vue/FullModal";
import { ICON_KEY, BUTTON_TYPE, default as noTextSvgButton } from "../../../../share/components/vue/NoTextSVGButton";
import wrappedTeleport from "../../../../share/components/vue/WrappedTeleport";
import customProgress from "../../../../share/components/vue/CustomProgress";
import obdDataMonitor from '../../../vue/obd/individual/dataMonitor/ObdDataMonitor';
import dmUtil, { TAB } from "../../../pages/dataMonitor/dataMonitorUtil";
import { createZoomTabHandlersForRefs, isResetZoomDisabled } from '../../../pages/dataMonitor/dataMonitorZoomUtil';
import { CsvFileWriter, getMeasurementFileName } from '../../../pages/dataMonitor/dataMonitorFileUtil';
import { WrappedUseI18n } from '../../../../share/plugins/I18nWrapper/index';
import { exportCsv } from './csv';

export default {
  components: {
    dataMonitor: obdDataMonitor,
    fullModal,
    noTextSvgButton,
    wrappedTeleport,
    customProgress
  },

  props: {
    isOnlyCsvExport: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  // eslint-disable-next-line
  setup(props, context) {
    const currentComponent = getCurrentInstance();

    // 他ファイルで$t, $teを使えるように追加
    const { t, te } = WrappedUseI18n();
    currentComponent.$t = t;
    currentComponent.$te = te;

    // 他ファイルで$modalDialogを使えるように追加 ※share/alert.jsのshowXXX()を使用するために使用
    currentComponent.$modalDialog = currentComponent.appContext.config.globalProperties.$modalDialog;

    // 子コンポーネントの参照
    const data_monitor = ref(null);
    // 親コンポーネントからのデータ
    const { isOnlyCsvExport } = toRefs(props);

    // リアクティブな変数
    const inPlaying = ref(false);
    const checkLines = ref(0);  // グラフ項目件数
    const currentTab = ref(TAB.LIST);
    const isZoomPanActive = ref(false);
    const highlightItems = ref([]);
    const currentPercentage = ref(0);
    const exportPercentage = ref(0);
    const dataMonitorFunctionSettings = new dmUtil.DataMonitorFunctionSettings(dmUtil.TYPE_CALLER.PLAY); // DM機能の設定インスタンス
    const recollectionAtInfo = ref([]); // OBDでは不使用
    const atOperatingStatusList = ref([]); // OBDでは不使用
    const isPhase6AtRecollection = ref(false); // OBDでは不使用
    // 全画面モーダルの表示有無
    const showModal = ref(false);
    const showSelect = ref(false);
    const showCsvProgress = ref(false);
    // Measureコンポーネントの描画完了したか否か。Measureコンポーネントへのteleportを描画完了まで待つ必要があるため、チェックする。
    const measureMounted = ref(false);
    // VuexのStoreの値・特定の算出の値
    const store = useStore();
    const cachedDetails = computed(() => store.state.cache.projectDetails).value;
    const btnDisabled = computed(() => !checkLines.value || isZoomPanActive.value); // ボタン使用不可制御
    const resetZoomBtnDisabled = computed(() => isResetZoomDisabled({
      inPlaying: inPlaying.value,
      hasCheckLines: Boolean(checkLines.value),
      currentTab: currentTab.value,
    }));
    // エクスポート可能かを管理するフラグ
    const ableToExport = ref(false);
    // 描画に使用せずコンポーネント内で保持するのみの値
    let measureId = '';
    let ecuName = '';

    /**
     * データモニタの再生画面を表示するためのデータを取得する
     * @param {object} selectedMeasureData 測定時に選択されていた項目
     * @param {string} diagnosticSystemId 再生対象の診断システムID
     */
    async function createObdDataMonitorMeasuresList(selectedMeasureData, diagnosticSystemId) {
      measureId = selectedMeasureData.measure_id;
      ecuName = selectedMeasureData.system;

      const currentMeasurementUuid = data_monitor.value.measurementUuid;
      const newMeasurementUuid = selectedMeasureData.session_id;

      if (currentMeasurementUuid === newMeasurementUuid) {
        // 言語変更時にデータの再取得を実施しない
        return;
      }

      data_monitor.value.startPlayApi(
        selectedMeasureData,
        diagnosticSystemId,
        cachedDetails.information.id,
        isOnlyCsvExport.value ? ableToCsvExport : () => { },
      );
      // 計測データ取得と同時に、モーダル表示
      if (!isOnlyCsvExport.value) {
        showModal.value = true;
      }
    }

    // グラフ設定
    function handleConfigureGraph() {
      data_monitor.value.handleConfigureGraph();
    }

    /**
     * メモボタン押下
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async function parentOpenMemoModal() {
      context.emit('openMemoModal');
    }

    /** 計測開始/停止状態切替 */
    function togglePlaying(flg) {
      if (flg === undefined || flg === null) {
        inPlaying.value = !inPlaying.value;
      } else {
        inPlaying.value = flg;
      }
      if (inPlaying.value) {
        isZoomPanActive.value = false;
      }
    }

    /** グラフ項目件数を変更 */
    function changeCheckValue(value) {
      checkLines.value = value;
    }

    /**
     * ズーム/パン状態を更新
     * @param {boolean} isActive
     */
    function zoomPanStateChanged(isActive) {
      isZoomPanActive.value = isActive;
    }

    /**
     * 戻るボタン押下時
     * DataRecollection.jsのbackToDetailsを呼び出している
     */
    function backSelect() {
      context.emit('backToDetails');
    }

    /**
     * 取得したデータポイントの更新時の処理。プログレスバーの取得率を更新する。
     * @param {number} gotTotalCount 取得済みのデータポイント数
     */
    function updateTotalCount(gotTotalCount) {
      if (!data_monitor.value || !inPlaying.value) {
        return;
      }
      const allCount = data_monitor.value.receivedDataPoints;
      currentPercentage.value = dmUtil.calculatePercentage(allCount, gotTotalCount);
      // 親コンポーネントのプログレスバー更新
      // データポイントの進捗とCSVエクスポートの進捗の両方を受け取る必要があるため、データポイントの進捗率をCSVエクスポートの進捗率の半分として更新する
      context.emit('updateExportPercentage', currentPercentage.value / 2);
    }

    /**
     * CSVエクスポートの進捗更新
     * @param {number} allCount 全体数
     * @param {number} currentCount 現在の数
     */
    function updateExportProgress(allCount, currentCount) {
      exportPercentage.value = dmUtil.calculatePercentage(allCount, currentCount);
      // 親コンポーネントのプログレスバー更新
      // データポイントの進捗とCSVエクスポートの進捗の両方を受け取る必要があるため、CSVエクスポートの進捗率をデータポイントの進捗率の半分と合わせて更新する
      context.emit('updateExportPercentage', currentPercentage.value / 2 + exportPercentage.value / 2);
    }

    /**
     * ファイルエクスポートボタン押下時の処理
     */
    async function csvExport(csvWriter = null) {
      // ファイル名の作成
      const targetMeasurement = cachedDetails.measures[0].obd.find(measure => measure.measure_id === measureId);
      const functionName = `${currentComponent.$t('fixed_resource.obd_menu_datamonitor')}${currentComponent.$t('fixed_resource.obd_menu')}`;
      const fileName = getMeasurementFileName(targetMeasurement.name ?? '', functionName, cachedDetails.information.vin ?? '');

      let csvWriterInstance = null;
      try {
        exportPercentage.value = 0;

        if (!isOnlyCsvExport.value) {
          showCsvProgress.value = true;
          // 再生画面からのCSVエクスポートの場合は、ファイル名を指定して新たにCsvFileWriterを作成する
          csvWriterInstance = new CsvFileWriter(fileName);
          await csvWriterInstance.init();
        } else {
          // 親コンポーネントでのCSVエクスポート中は、渡されたCsvFileWriterを使用して、再生画面のプログレスバーは表示しない
          csvWriterInstance = csvWriter;
        }

        const exportCsvParams = {
          csvFileWriter: csvWriterInstance,
          currentComponent,
          dataMonitorComponent: data_monitor,
          projectDetails: cachedDetails,
          measurementId: measureId,
          ecuName,
          errorDialogCallback: () => backSelect(),
          updateExportProgress,
        };
        await exportCsv(exportCsvParams);
      } catch (error) {
        if (CsvFileWriter.isUserCancel(error)) {
          // ユーザーがCSVの書き込みをキャンセルしたら何もせず終了
          return;
        }
        throw error;
      } finally {
        showCsvProgress.value = false;
        csvWriterInstance && await csvWriterInstance.close();
      }
    }

    /**
     * 計測データ取得処理の停止
     */
    async function stopFetchingMeasurement() {
      if (inPlaying.value) {
        // 直前の再生データ取得中であれば停止する
        await data_monitor.value.stopFetchingMeasurement();
        inPlaying.value = false;
        currentPercentage.value = 0;
      }
    }

    /**
     * 最大化ボタン押下処理
     */
    function maximum() {
      showModal.value = true;
    }

    /**
     * 最小化ボタン押下処理
     */
    function minimum() {
      showModal.value = false;
    }

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    function modalContentId() {
      return `full-modal-content-obd-${dataMonitorFunctionSettings.typeCaller}`;
    }

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    function graphSettingsDestinationId() {
      return `graph-settings-destination-obd-${dataMonitorFunctionSettings.typeCaller}`;
    }

    /**
     * 子コンポーネントからDataMonitorMeasureのMount・Unmountの通知を受け取る
     * @param {boolean} isMounted Mountされたか否か
     */
    function measureMountedEvent(isMounted) {
      measureMounted.value = isMounted;
      if (!isMounted) {
        // 計測画面のコンポーネントが破棄されたら、計測画面を離れたと判断して全画面モーダルも閉じる
        showModal.value = false;
      }
    }

    function ableToCsvExport() {
      ableToExport.value = true;
    }

    onBeforeUnmount(async () => {
      new dmUtil.Cache().destroy();
      // 画面遷移前に再生データ取得中であれば停止する
      await stopFetchingMeasurement();
    });

    onBeforeRouteLeave((to, from, next) => {
      showSelect.value = true;
      next();
    });

    return {
      ...mapActions('auth', ['logout']),
      ICON_KEY,
      BUTTON_TYPE,
      data_monitor,
      inPlaying,
      btnDisabled,
      resetZoomBtnDisabled,
      isZoomPanActive,
      highlightItems,
      currentPercentage,
      exportPercentage,
      dataMonitorFunctionSettings,
      recollectionAtInfo,
      atOperatingStatusList,
      isPhase6AtRecollection,
      showModal,
      showCsvProgress,
      measureMounted,
      showSelect,
      isOnlyCsvExport,
      ableToExport,
      createObdDataMonitorMeasuresList,
      handleConfigureGraph,
      ...createZoomTabHandlersForRefs(data_monitor, currentTab, () => resetZoomBtnDisabled.value),
      parentOpenMemoModal,
      togglePlaying,
      changeCheckValue,
      zoomPanStateChanged,
      backSelect,
      csvExport,
      updateTotalCount,
      stopFetchingMeasurement,
      maximum,
      minimum,
      modalContentId,
      graphSettingsDestinationId,
      measureMountedEvent,
      ableToCsvExport,
    };
  }
};